/**
 * "Zum Kalender hinzufügen" affordance for the B2C tracking bottom sheet
 * (PLA-480).
 *
 * Icon-only, so it sits in the sticky header without competing with the phase
 * label. Tapping opens a two-row popover: Google Calendar via its template URL
 * (one tap on Android and desktop Chrome) and an `.ics` download for Apple
 * Calendar and Outlook (the path iPhone Safari users need).
 *
 * All event construction lives in `utils/calendar-export` — this component only
 * does the web-specific DOM work.
 */

import { FC, useCallback, useState } from "react"
import { View, ViewStyle, TextStyle, TouchableOpacity } from "react-native"
import * as Sentry from "@sentry/react"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Text } from "@/components/Text"
import { colors, spacing } from "@/theme"
import {
  ICS_FILE_NAME,
  ICS_MIME_TYPE,
  toGoogleCalendarUrl,
  toIcsFile,
  type CalendarEvent,
} from "@/utils/calendar-export"

interface AddToCalendarButtonProps {
  event: CalendarEvent
  /** Shrinks the hit area and icon for the narrow mobile header. */
  isMobile?: boolean
}

export const AddToCalendarButton: FC<AddToCalendarButtonProps> = ({ event, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleGoogle = useCallback(() => {
    setIsOpen(false)
    if (typeof window === "undefined") return
    window.open(toGoogleCalendarUrl(event), "_blank", "noopener,noreferrer")
  }, [event])

  const handleIcs = useCallback(() => {
    setIsOpen(false)
    if (typeof window === "undefined" || typeof document === "undefined") return

    let objectUrl: string | undefined
    try {
      const blob = new Blob([toIcsFile(event)], { type: ICS_MIME_TYPE })
      objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = objectUrl
      anchor.download = ICS_FILE_NAME
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
    } catch (error) {
      // A failed Blob/anchor download is a genuine defect, not an expected
      // rejection — ADR-0036 tier 3.
      Sentry.captureException(error, {
        tags: { surface: "carassist", operation: "calendar_ics_download" },
        extra: { calendarUid: event.uid },
      })
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [event])

  const iconSize = isMobile ? 20 : 22

  return (
    <>
      {/* Invisible backdrop so a tap anywhere dismisses the popover. */}
      {isOpen && (
        <TouchableOpacity
          style={$backdrop}
          activeOpacity={1}
          onPress={() => setIsOpen(false)}
          accessibilityLabel="Schließen"
        />
      )}

      <View style={$anchor}>
        <TouchableOpacity
          onPress={() => setIsOpen((open) => !open)}
          style={[$iconButton, isMobile && $iconButtonMobile]}
          accessibilityLabel="Zum Kalender hinzufügen"
          accessibilityRole="button"
          // @ts-ignore - Web-specific
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          title="Zum Kalender hinzufügen"
        >
          {/* MaterialIcons has no calendar-plus glyph, so compose one: `event`
              with a small `add` badge. Keeps the app on a single icon font —
              MaterialCommunityIcons' calendar-plus would cost a ~1.1 MB second
              font download on a B2C mobile page for one glyph. */}
          <View style={$iconStack}>
            <Icon name="event" size={iconSize} color={colors.primary} />
            <View style={[$addBadge, isMobile && $addBadgeMobile]}>
              <Icon name="add" size={isMobile ? 10 : 11} color={colors.surface} />
            </View>
          </View>
        </TouchableOpacity>

        {/* Desktop-only hover tooltip, mirroring the drag handle's pattern. */}
        {isHovered && !isOpen && !isMobile && (
          <View style={$tooltip} pointerEvents="none">
            <Text style={$tooltipText}>Zum Kalender hinzufügen</Text>
          </View>
        )}

        {isOpen && (
          <View style={$popover}>
            <TouchableOpacity style={$popoverRow} onPress={handleGoogle}>
              <Icon name="event-available" size={18} color={colors.primary} />
              <Text style={$popoverRowText}>Google Kalender</Text>
              <Icon name="open-in-new" size={14} color={colors.textDim} />
            </TouchableOpacity>

            <View style={$popoverDivider} />

            <TouchableOpacity style={$popoverRow} onPress={handleIcs}>
              <Icon name="download" size={18} color={colors.primary} />
              <Text style={$popoverRowText}>Apple / Outlook</Text>
              <Text style={$popoverRowHint}>.ics</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </>
  )
}

/**
 * Viewport-fixed, not absolute. Both this and $anchor are children of the
 * shrink-wrapped anchor View in the sticky header, so `absolute` + zero insets
 * would resolve against a ~36 px box and catch nothing — taps outside the
 * popover would neither dismiss it nor be blocked from reaching the controls
 * underneath.
 */
const $backdrop: ViewStyle = {
  // @ts-ignore - Web-specific: RNW passes `fixed` through to CSS.
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  // Below $anchor so the popover itself stays clickable above the backdrop.
  zIndex: 1,
}

const $anchor: ViewStyle = {
  position: "relative",
  zIndex: 2,
}

const $iconButton: ViewStyle = {
  width: 36,
  height: 36,
  borderRadius: 18,
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 56, 47, 0.08)",
}

const $iconButtonMobile: ViewStyle = {
  width: 32,
  height: 32,
  borderRadius: 16,
}

const $iconStack: ViewStyle = {
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
}

/**
 * The "+" half of the composed calendar-plus. Sits on the glyph's lower-right
 * with a surface-coloured ring so it reads as a badge rather than as part of
 * the calendar. Offsets are small enough to stay inside the button's circle
 * (badge right edge lands ~2px inside it at both sizes).
 */
const $addBadge: ViewStyle = {
  position: "absolute",
  right: -5,
  bottom: -4,
  width: 15,
  height: 15,
  borderRadius: 8,
  backgroundColor: colors.primary,
  alignItems: "center",
  justifyContent: "center",
  borderWidth: 1.5,
  borderColor: colors.surface,
}

const $addBadgeMobile: ViewStyle = {
  right: -4,
  bottom: -3,
  width: 13,
  height: 13,
  borderRadius: 7,
}

const $tooltip: ViewStyle = {
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: 6,
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  paddingVertical: 6,
  paddingHorizontal: 10,
  borderRadius: 6,
  // @ts-ignore - Web-specific
  whiteSpace: "nowrap",
}

const $tooltipText: TextStyle = {
  color: colors.surface,
  fontSize: 13,
}

const $popover: ViewStyle = {
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: 8,
  minWidth: 210,
  backgroundColor: colors.surface,
  borderRadius: 12,
  paddingVertical: spacing.xxs,
  overflow: "hidden",
  // @ts-ignore - Web-specific
  boxShadow: "0 12px 32px rgba(17, 30, 44, 0.2), 0 2px 6px rgba(17, 30, 44, 0.08)",
  elevation: 10,
}

const $popoverRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.sm,
}

const $popoverRowText: TextStyle = {
  flex: 1,
  fontSize: 14,
  fontWeight: "600",
  color: colors.text,
}

const $popoverRowHint: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
}

const $popoverDivider: ViewStyle = {
  height: 1,
  backgroundColor: colors.separator,
  marginHorizontal: spacing.sm,
}
